import React, { useEffect, useState } from 'react';
import { DialogUI, FormButtonPanelUI } from '../../Interface';
import { Field, Form, Formik } from 'formik';
import dayjs from 'dayjs';
import Yup from '../../../utils/yup';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import useUsers from '../../../context/UsersContext';
import employeeAttendenceService from '../../../service/employeeAttendence.service';
import masterDataService from '../../../service/masterData.service';
import { generateAutocompleteItems } from '../../../utils/helper';
import EmployeeAttendanceFields from '../EmployeeAttendanceFields';
import { Box } from '@mui/material';
import EmployeeAttendanceFieldsDialog from './EmployeeAttendanceFieldsDialog';
import EmployeeAttendanceLine from '../EmployeeAttendanceLine';

const EmployeeAttendanceDialog = ({ onClose, dialogData }) => {
  const { user } = useUsers();
  const { open, data } = dialogData;
  const [initValues, setInitValues] = useState({});
  const [autocompleteOptions, setAutocompleteOptions] = useState({
    project: [],
    employees: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const defFieldsData = {
    open: false,
    index: null,
  };
  const [fieldsDialogData, setFieldsDialogData] = useState(defFieldsData);

  const defLine = {
    id: null,
    isNew: true,
    employeeId: null,
    rating: null,
    fromTime: null,
    toTime: null,
    timeSpent: null,
  };

  const defValues = {
    projectId: null,
    date: dayjs(),
    employeeAttendanceLine: [],
  };

  useEffect(() => {
    initDefaultValues();
    setInitValues(defValues);
  }, []);

  useEffect(() => {
    if (Object.keys(data).length > 0 && !isLoading) {
      setInitValues((prev) => ({
        ...prev,
        ...data,
      }));
    }
  }, [data, isLoading]);

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(),
    projectId: Yup.string().required(),
    employeeAttendanceLine: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required(),
        timeSpent: Yup.number().required().positive().max(24),
        rating: Yup.number().nullable().min(0).max(9.99),
        fromTime: Yup.string().required(),
        toTime: Yup.string().required(),
      })
    ),
  });

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          employees: resp[1],
        };

        const acOptions = {
          project: generateAutocompleteItems(aData.project, 'projectName', 'projectId'),
          employees: generateAutocompleteItems(aData.employees, 'name', 'id'),
        };
        setAutocompleteOptions(acOptions);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const insertObject = (values) => {
    const insertObject = {
      ...values,
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      userId: user.userId,
      createdBy: user.userId,
    };

    employeeAttendenceService
      .createRow(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        onClose();
      })

      .finally(() => {
        //@TODO loader
      });
  };

  return (
    <DialogUI
      open={open}
      headerContent="Lejelentés"
      fullWidth={true}
      onClose={onClose}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1000px',
            mx: 1,
          },
        },
        '& .MuiDialogContent-root': {
          px: 1,
        },
      }}
    >
      <Box mt="1rem">
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={insertObject}
          enableReinitialize
        >
          <Form>
            <EmployeeAttendanceFields fromReportDialog={true} autocompleteOptions={autocompleteOptions} />
            <EmployeeAttendanceFieldsDialog
              dialogData={fieldsDialogData}
              onClose={() => setFieldsDialogData(defFieldsData)}
              autocompleteOptions={autocompleteOptions}
              defaultField={defLine}
            />
            <EmployeeAttendanceLine
              autocompleteOptions={autocompleteOptions}
              setFieldsDialogData={setFieldsDialogData}
              fromReportDialog={true}
            />
            <FormButtonPanelUI onCancelClick={onClose} />
          </Form>
        </Formik>
      </Box>
    </DialogUI>
  );
};

export default EmployeeAttendanceDialog;
